import React from "react";
import "./OutletPage.css";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg    from "../image/gen-ice-hero.png";
import expiryImg  from "../image/gen-ice-expiry.png";
import stockImg   from "../image/gen-ice-stock.png";
import quickImg   from "../image/Quick-Billing_lg.avif";
import varImg     from "../image/Variations-And-Add-Ons_lg.avif";
import reportImg  from "../image/Inventory-Reports_lg.avif";
import loyaltyImg from "../image/Loyalty-wallet_md.avif";
import demoImg    from "../image/DemoForm_img.png";

const GRADIENT = "linear-gradient(135deg, #22c55e, #16a34a)";
const GLOW     = "rgba(34,197,94,0.3)";

const css = `
  .op-icecream { --op-accent:#22c55e; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(34,197,94,0.1); --op-accent-30:rgba(34,197,94,0.3);
    --op-accent-05:rgba(34,197,94,0.05); }
`;

export default function IceCreamOutletPage() {
  return (
    <div className="op-page op-icecream">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Ice Cream & Desserts" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Ice Cream &amp; Desserts POS
          </div>
          <h1 className="op-hero__title">
            Fresh stock,<br />
            <span className="op-accent-text">zero waste</span>
          </h1>
          <p className="op-hero__subtitle">
            Expiry tracking, stock alerts, flavor variations and quick billing — KadaiPOS keeps your dessert counter running clean and profitable.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost"><Play size={16} /> See It In Action</button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Expiry Alerts</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Auto</span>
              <span className="op-hero__stat-label">Stock Deduction</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">∞</span>
              <span className="op-hero__stat-label">Flavor Variants</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Zero</span>
              <span className="op-hero__stat-label">Expiry Surprise</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">Core Features</div>
          <h2 className="op-section-title">Built for freshness and speed</h2>
          <p className="op-section-subtitle">
            Ice cream and desserts have two enemies: warm temperatures and expired stock. KadaiPOS fights both.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Wide – Expiry */}
          <div className="op-card op-card--wide">
            <img src={expiryImg} alt="Item Expiry Monitoring" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">⏰ Expiry Monitoring</div>
              <h3 className="op-card__title">Never serve an expired product — automatic early warnings</h3>
              <p className="op-card__desc">
                Every inventory batch is tagged with its expiry date. KadaiPOS alerts you 3 days, 1 day, and on the day of expiry — so you can mark down or remove items before it's too late.
              </p>
              <ul className="op-card__list">
                <li>Batch-wise expiry date entry at stock intake</li>
                <li>Alert escalation: 3 days → 1 day → day-of</li>
                <li>Auto-highlight expired items on billing screen</li>
                <li>Expiry loss report for accounting</li>
              </ul>
            </div>
          </div>

          {/* Narrow accent – Stock */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>📉</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Low Stock Alerts</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                Set reorder levels per item. When stock drops below the threshold, the billing screen flashes an alert before you run out mid-rush.
              </p>
              <ul className="op-card__list">
                <li>Per-item reorder threshold</li>
                <li>Alert on billing screen during service</li>
                <li>Auto purchase request to supplier</li>
              </ul>
            </div>
          </div>

          {/* Half – Stock Management image */}
          <div className="op-card op-card--half">
            <img src={stockImg} alt="Stock Management" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📦 Stock Management</div>
              <h3 className="op-card__title">Know what's in your freezer — down to the tub</h3>
              <p className="op-card__desc">Opening stock, closing stock, wastage entry and purchase — all tracked per day. No more guesswork on what to order.</p>
            </div>
          </div>

          {/* Half – Variations */}
          <div className="op-card op-card--half">
            <img src={varImg} alt="Variations" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">🍦 Flavor Variants</div>
              <h3 className="op-card__title">Scoop, cup, cone, sundae — one item, many forms</h3>
              <p className="op-card__desc">Configure serving size, flavor, toppings, and cone type as variants. Every combination tracked and priced correctly on the bill.</p>
            </div>
          </div>

          {/* Full – Quick Billing */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <img src={quickImg} alt="Quick Billing" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">⚡ Quick Billing</div>
              <h3 className="op-card__title">Long queue? Bill and move — under 8 seconds per customer</h3>
              <p className="op-card__desc">
                Counter billing at a dessert shop needs to be fast. KadaiPOS's one-tap search, variant picker, and payment in a single screen keeps the queue moving.
              </p>
              <ul className="op-card__list">
                <li>Item search by name, shortcode, or barcode</li>
                <li>Variant &amp; topping selection in 2 taps</li>
                <li>Cash, UPI, card — all payment modes on one screen</li>
                <li>WhatsApp / SMS bill — no printer needed</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Reports &amp; Loyalty</div>
            <h2 className="op-section-title">Sell more, waste less, retain more</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={reportImg} alt="Inventory Reports" />
              <div className="op-deepdive__img-badge">📊 Inventory Reports</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Daily inventory reports — know your costs before they spiral</h3>
              <p>
                Closing stock, wastage, item-wise consumption — all in one daily report. Spot which flavors move and which ones expire, so you order smarter next time.
              </p>
              <ul className="op-check-list">
                {[
                  "Item-wise daily sales quantity &amp; revenue",
                  "Wastage vs sold ratio to minimize losses",
                  "Slow-moving stock identification",
                  "Seasonal demand comparison (last week vs this week)",
                  "Export to Excel for supplier negotiations"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={loyaltyImg} alt="Loyalty Wallet" />
              <div className="op-deepdive__img-badge">🎁 Loyalty</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Keep sweet-tooths coming back with a loyalty wallet</h3>
              <p>
                Every purchase earns points. Kids and families return for their free scoop reward. Seasonal campaigns ("Mango Festival 20% off") go out by SMS in minutes.
              </p>
              <ul className="op-check-list">
                {[
                  "Auto-points on every bill — fully configurable",
                  "Wallet balance shown at billing for redemption",
                  "Birthday treat campaigns auto-sent by SMS",
                  "Combo offers to increase average bill value",
                  "Customer visit frequency analytics"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "🧮", title: "Weight-based Billing", desc: "Bill desserts by weight — auto-calculate price on the scale reading." },
            { icon: "🏷️", title: "Seasonal Menus", desc: "Switch between summer and winter menus with one toggle. No re-entry needed." },
            { icon: "📣", title: "Offer Engine", desc: "Run combo offers — 'Any 2 scoops + waffle = ₹199' set in seconds." },
            { icon: "📱", title: "Digital Bill", desc: "Send bill via WhatsApp or SMS — great for takeaway and no-printer counters." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Stop losing money to expired stock &amp; missed reorders</h2>
            <p>KadaiPOS alerts you before it costs you. Book a free demo and see live expiry tracking and stock alerts in action.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Shop name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
